import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { useBreakpointValue } from "@shared/hooks/useBreakpointValue";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { useRef } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { AnalyticsService } from "@shared/services/analytics";
import {
  ProductAddonsModalContent,
  VariantQuantityMap,
} from "./ProductAddonsModalContent";
import { addProductToBasket } from "./ProductStep";

interface ProductAddonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ProductTypeSerializer;
  modalHeight?: string;
  analyticsSource?: string;
}

const ProductAddonsModal = ({
  isOpen,
  onClose,
  product,
  modalHeight,
  analyticsSource,
}: ProductAddonsModalProps) => {
  const { t } = useTranslation("purchases");
  const items = useRef<VariantQuantityMap>({});
  const publicBasket = usePublicBasket();
  const footerCancelButtonDisplay = useBreakpointValue({
    base: "none",
    sm: "block",
  }) as "none" | "block";
  const handleDone = async () => {
    await addProductToBasket(items.current, publicBasket);
    AnalyticsService.buttonClick({
      label: "addons",
      custom_params: {
        datum: "add_to_cart",
        type: "product",
        id: product.id,
        ...(analyticsSource && { source: analyticsSource }),
      },
    });
    onClose();
  };
  return (
    <Modal
      isOpen={isOpen}
      isCentered
      onClose={onClose}
      size="3xl"
      variant="interactive"
      modalContentProps={
        modalHeight ? { maxH: modalHeight, minH: modalHeight } : undefined
      }
    >
      <Modal.Header>{t("Select your product!")}</Modal.Header>

      <Modal.Body padding="none" overflowY="auto">
        <ProductAddonsModalContent
          onChange={(c) => {
            items.current = c;
          }}
          product={product}
        />
      </Modal.Body>

      <Modal.Footer>
        <Flex direction="row-reverse" justifyContent="space-between" w="full">
          <Button
            data-testid="add-add-on-product"
            variant="solid"
            onClick={handleDone}
          >
            {t("Add")}
          </Button>
          <Button.Ghost
            data-testid="cancel-add-add-on-product"
            display={footerCancelButtonDisplay}
            onClick={onClose}
          >
            {t("Cancel", { ns: "common" })}
          </Button.Ghost>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductAddonsModal;
